import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { Search, Filter, ArrowRight, FileText, ChevronDown, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { useVertical } from '../../../../context/VerticalContext';
import InvoicePreview from '../InvoicePreview';
import PaymentModal from '../PaymentModal';
import toast from 'react-hot-toast';
import { useConfirm } from '../../../../components/ui/ConfirmProvider';

type Invoice = {
    id: string;
    invoice_number: string;
    created_at: string;
    issued_at: string | null;
    current_date: string;
    customer: { full_name: string } | null;
    vehicle: { vrm: string; make: string; model: string } | null;
    total_gross: number;
    amount_paid: number;
    balance_due: number;
    status: 'draft' | 'issued' | 'partially_paid' | 'paid' | 'void' | 'refunded';
    deleted_at: string | null;
};

export default function InvoiceList() {
    const { activeOrgId: orgId } = useVertical();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const confirm = useConfirm();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    // Bin Mode
    const [isBinView, setIsBinView] = useState(false);

    // View/Payment State
    const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
    const [paymentInvoice, setPaymentInvoice] = useState<any>(null);
    const [paymentMode, setPaymentMode] = useState<'card' | 'cash' | 'part'>('card');

    useEffect(() => {
        if (!orgId) return;
        fetchInvoices();
    }, [orgId, statusFilter, isBinView]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('invoices')
                .select(`
                    *,
                    customer:customers(full_name),
                    vehicle:vehicles(vrm, make, model)
                `)
                .eq('org_id', orgId)
                .order('created_at', { ascending: false });

            if (isBinView) {
                query = query.not('deleted_at', 'is', null);
            } else {
                query = query.is('deleted_at', null);
                if (statusFilter !== 'all') {
                    query = query.eq('status', statusFilter);
                }
            }

            const { data, error } = await query;

            if (error) throw error;
            setInvoices(data || []);
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSoftDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await confirm({ title: 'Move invoice to the bin?', message: 'You can restore it from the Bin later.', confirmText: 'Move to bin' })) return;

        try {
            const { error } = await supabase
                .from('invoices')
                .update({ deleted_at: new Date().toISOString() })
                .eq('id', id);

            if (error) throw error;
            fetchInvoices();
        } catch (err) {
            console.error('Error deleting invoice:', err);
            toast.error('Failed to delete invoice');
        }
    };

    const handleRestore = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const { error } = await supabase
                .from('invoices')
                .update({ deleted_at: null })
                .eq('id', id);

            if (error) throw error;
            fetchInvoices();
        } catch (err) {
            console.error('Error restoring invoice:', err);
            toast.error('Failed to restore invoice');
        }
    };

    const handleVoidAndCorrect = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await confirm({ title: 'Void and redraft?', message: 'The current invoice is voided and a new draft is created from it.', confirmText: 'Void and redraft', isDangerous: true })) return;
        try {
            const { error, data } = await supabase.rpc('fn_void_and_copy_invoice', { p_invoice_id: id });
            if (error) throw error;
            fetchInvoices();
            setViewInvoiceId(data as string); // Open the new draft
        } catch (err) {
            console.error('Error voiding invoice:', err);
            toast.error('Failed to void invoice');
        }
    };

    const handleWriteOff = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await confirm({ title: 'Write this invoice off?', message: 'A Bad Debt expense is recorded in your ledger. This affects your figures.', confirmText: 'Write off', isDangerous: true })) return;
        try {
            const { error } = await supabase.rpc('fn_write_off_invoice', { p_invoice_id: id });
            if (error) throw error;
            fetchInvoices();
        } catch (err) {
            console.error('Error writing off invoice:', err);
            toast.error('Failed to write off invoice');
        }
    };

    const handlePermanentDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await confirm({ title: 'Delete permanently?', message: 'This invoice and its linked ledger entries are removed for good. This cannot be undone.', confirmText: 'Delete for good', isDangerous: true })) return;

        // MVP: Just delete the invoice row. Ledger consistency might need a cascaded delete or separate handling.
        // Assuming Postgres ON DELETE CASCADE is set up or we accept orphan ledger entries for now (not ideal for strict audit).

        try {
            const { error } = await supabase
                .from('invoices')
                .delete()
                .eq('id', id);

            if (error) throw error;
            fetchInvoices();
        } catch (err) {
            console.error('Error permanently deleting invoice:', err);
            toast.error('Failed to delete invoice permanently');
        }
    };

    const filteredInvoices = invoices.filter(inv => {
        const searchLower = searchTerm.toLowerCase();
        return (
            inv.invoice_number.toLowerCase().includes(searchLower) ||
            inv.customer?.full_name.toLowerCase().includes(searchLower) ||
            inv.vehicle?.vrm.toLowerCase().includes(searchLower)
        );
    });

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
        }).format(amount);
    };

    return (
        <div className="space-y-6">
            {/* Filters Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center bg-panel p-4 rounded-xl border border-tint/5">
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-3" />
                    <input
                        type="text"
                        placeholder="Search invoices, customers, VRM..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-scrim/20 border border-tint/10 rounded-lg text-sm text-fg focus:outline-none focus:border-accent transition-colors"
                    />
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    {!isBinView && (
                        <div className="relative flex-1 sm:flex-none">
                            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-3" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full sm:w-48 pl-10 pr-8 py-2 bg-scrim/20 border border-tint/10 rounded-lg text-sm text-fg focus:outline-none focus:border-accent appearance-none cursor-pointer"
                            >
                                <option value="all">All Statuses</option>
                                <option value="draft">Draft</option>
                                <option value="issued">Issued</option>
                                <option value="partially_paid">Partially Paid</option>
                                <option value="paid">Paid</option>
                                <option value="void">Void</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-3 pointer-events-none" />
                        </div>
                    )}

                    <button
                        onClick={() => setIsBinView(!isBinView)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${isBinView
                            ? 'bg-danger/10 border-danger/50 text-danger'
                            : 'bg-tint/5 border-tint/10 text-fg-3 hover:text-fg hover:bg-tint/10'
                            }`}
                        title={isBinView ? "Exit Recycle Bin" : "View Deleted Invoices"}
                    >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">{isBinView ? 'Exit Bin' : 'Recycle Bin'}</span>
                    </button>
                </div>
            </div>

            {/* Invoices List */}
            <div className={`bg-panel border rounded-xl overflow-hidden ${isBinView ? 'border-danger/20' : 'border-tint/5'}`}>
                {isBinView && (
                    <div className="bg-danger/10 px-4 py-2 border-b border-danger/20 flex items-center gap-2 text-sm text-danger">
                        <AlertTriangle className="w-4 h-4" />
                        Viewing Recycle Bin — Items here are not included in financial reports.
                    </div>
                )}

                {loading ? (
                    <div className="p-8 text-center text-fg-3">Loading invoices...</div>
                ) : filteredInvoices.length === 0 ? (
                    <div className="p-12 text-center text-fg-4">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-tint/5 flex items-center justify-center">
                            {isBinView ? <Trash2 className="w-8 h-8 opacity-50" /> : <FileText className="w-8 h-8 opacity-50" />}
                        </div>
                        <p className="text-lg font-medium text-fg mb-1">{isBinView ? 'Bin is empty' : 'No invoices found'}</p>
                        {!isBinView && <p className="text-sm">Try adjusting your search or filters</p>}
                    </div>
                ) : (
                    <>
                        {/* Mobile Card Layout */}
                        <div className="block md:hidden divide-y divide-tint/5">
                            {filteredInvoices.map((inv) => (
                                <div
                                    key={inv.id}
                                    className="p-4 group hover:bg-tint/[0.02] transition-colors cursor-pointer flex flex-col gap-3"
                                    onClick={() => setViewInvoiceId(inv.id)}
                                >
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <span className="font-mono font-medium text-fg group-hover:text-accent transition-colors">
                                                {inv.invoice_number}
                                            </span>
                                            <div className="text-xs text-fg-4 mt-1">
                                                {new Date(inv.issued_at || inv.created_at).toLocaleDateString()}
                                            </div>
                                        </div>
                                        <InvoiceStatusBadge status={inv.status} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <div className="text-xs text-fg-4 uppercase">Customer</div>
                                            <div className="text-fg-2">{inv.customer?.full_name || 'Walk-in'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-fg-4 uppercase">Vehicle</div>
                                            {inv.vehicle ? (
                                                <div className="text-fg-2">
                                                    <span>{inv.vehicle.vrm}</span>
                                                    <span className="text-xs text-fg-4 ml-1">{inv.vehicle.make}</span>
                                                </div>
                                            ) : <div className="text-fg-3">-</div>}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center pt-2 border-t border-tint/5">
                                        <div>
                                            <div className="text-xs text-fg-4 uppercase font-bold text-fg">Total Value</div>
                                            <div className="text-lg text-accent font-medium font-mono">{formatCurrency(inv.total_gross)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-fg-4 uppercase">Balance</div>
                                            <div className={`${inv.balance_due > 0 ? 'text-danger' : 'text-fg-4'} font-medium`}>
                                                {formatCurrency(inv.balance_due)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-tint/5">
                                        {isBinView ? (
                                            <>
                                                <button
                                                    className="p-2 text-success hover:text-success hover:bg-success/10 rounded-lg transition-colors flex items-center gap-2"
                                                    onClick={(e) => handleRestore(inv.id, e)}
                                                >
                                                    <RotateCcw className="w-5 h-5" />
                                                    <span className="text-xs font-bold">RESTORE</span>
                                                </button>
                                                <button
                                                    className="p-2 text-danger hover:text-danger hover:bg-danger/10 rounded-lg transition-colors flex items-center gap-2"
                                                    onClick={(e) => handlePermanentDelete(inv.id, e)}
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                    <span className="text-xs font-bold">DELETE</span>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="p-2 text-fg-3 hover:text-fg hover:bg-tint/10 rounded-lg transition-colors flex items-center gap-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setViewInvoiceId(inv.id);
                                                    }}
                                                >
                                                    <ArrowRight className="w-5 h-5" />
                                                    <span className="text-xs font-bold">VIEW</span>
                                                </button>
                                                {inv.status === 'issued' && (
                                                    <button
                                                        className="p-2 text-fg-3 hover:text-warning hover:bg-warning/10 rounded-lg transition-colors flex items-center gap-2"
                                                        onClick={(e) => handleVoidAndCorrect(inv.id, e)}
                                                        title="Void & Correct"
                                                    >
                                                        <RotateCcw className="w-5 h-5" />
                                                        <span className="text-xs font-bold hidden sm:inline">VOID</span>
                                                    </button>
                                                )}
                                                {(inv.status === 'issued' || inv.status === 'partially_paid') && (
                                                    <button
                                                        className="p-2 text-fg-3 hover:text-chart-5 hover:bg-chart-5/10 rounded-lg transition-colors flex items-center gap-2"
                                                        onClick={(e) => handleWriteOff(inv.id, e)}
                                                        title="Write off"
                                                    >
                                                        <AlertTriangle className="w-5 h-5" />
                                                        <span className="text-xs font-bold hidden sm:inline">WRITE OFF</span>
                                                    </button>
                                                )}
                                                {['draft', 'void', 'written_off'].includes(inv.status) && (
                                                    <button
                                                        className="p-2 text-fg-3 hover:text-danger hover:bg-danger/10 rounded-lg transition-colors flex items-center gap-2"
                                                        onClick={(e) => handleSoftDelete(inv.id, e)}
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                        <span className="text-xs font-bold">BIN</span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop Table Layout */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-tint/5 text-xs text-fg-3 uppercase tracking-wider">
                                        <th className="px-6 py-4 font-medium">Invoice</th>
                                        <th className="px-6 py-4 font-medium">Date</th>
                                        <th className="px-6 py-4 font-medium">Customer</th>
                                        <th className="px-6 py-4 font-medium">Vehicle</th>
                                        <th className="px-6 py-4 font-medium text-right">Amount</th>
                                        <th className="px-6 py-4 font-medium text-right">Balance</th>
                                        <th className="px-6 py-4 font-medium text-center">Status</th>
                                        <th className="px-6 py-4 font-medium text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-tint/5">
                                    {filteredInvoices.map((inv) => (
                                        <tr
                                            key={inv.id}
                                            className="group hover:bg-tint/[0.02] transition-colors cursor-pointer"
                                            onClick={() => setViewInvoiceId(inv.id)}
                                        >
                                            <td className="px-6 py-4">
                                                <span className="font-mono font-medium text-fg group-hover:text-accent transition-colors">
                                                    {inv.invoice_number}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-fg-2">
                                                {new Date(inv.issued_at || inv.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-fg-2">
                                                {inv.customer?.full_name || 'Walk-in'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-fg-3">
                                                {inv.vehicle ? (
                                                    <div className="flex flex-col">
                                                        <span className="text-fg">{inv.vehicle.vrm}</span>
                                                        <span className="text-xs text-fg-4">{inv.vehicle.make} {inv.vehicle.model}</span>
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right text-fg font-medium">
                                                {formatCurrency(inv.total_gross)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right">
                                                <span className={`${inv.balance_due > 0 ? 'text-danger' : 'text-fg-4'}`}>
                                                    {formatCurrency(inv.balance_due)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <InvoiceStatusBadge status={inv.status} />
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {isBinView ? (
                                                        <>
                                                            <button
                                                                className="p-2 text-success hover:text-success hover:bg-success/10 rounded-lg transition-colors"
                                                                onClick={(e) => handleRestore(inv.id, e)}
                                                                title="Restore Invoice"
                                                            >
                                                                <RotateCcw className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                className="p-2 text-danger hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
                                                                onClick={(e) => handlePermanentDelete(inv.id, e)}
                                                                title="Delete Permanently"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="p-2 text-fg-3 hover:text-fg hover:bg-tint/10 rounded-lg transition-colors fd-hover-reveal"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setViewInvoiceId(inv.id);
                                                                }}
                                                            >
                                                                <ArrowRight className="w-4 h-4" />
                                                            </button>
                                                            {inv.status === 'issued' && (
                                                                <button
                                                                    className="p-2 text-fg-3 hover:text-warning hover:bg-warning/10 rounded-lg transition-colors fd-hover-reveal"
                                                                    onClick={(e) => handleVoidAndCorrect(inv.id, e)}
                                                                    title="Void and create correcting draft"
                                                                >
                                                                    <RotateCcw className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            {(inv.status === 'issued' || inv.status === 'partially_paid') && (
                                                                <button
                                                                    className="p-2 text-fg-3 hover:text-chart-5 hover:bg-chart-5/10 rounded-lg transition-colors fd-hover-reveal"
                                                                    onClick={(e) => handleWriteOff(inv.id, e)}
                                                                    title="Write Off as Bad Debt"
                                                                >
                                                                    <AlertTriangle className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            {['draft', 'void', 'written_off'].includes(inv.status) && (
                                                                <button
                                                                    className="p-2 text-fg-3 hover:text-danger hover:bg-danger/10 rounded-lg transition-colors fd-hover-reveal"
                                                                    onClick={(e) => handleSoftDelete(inv.id, e)}
                                                                    title="Move to Bin"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* View/Review Modal */}
            {/* Using a custom modal wrapper or reusing the one from InvoiceGenerator? 
                 InvoicePreview usually is embedded. Let's create a simple modal wrapper here. */}
            {viewInvoiceId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/80 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-panel w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl border border-tint/10 shadow-2xl animate-in zoom-in-95">
                        <div className="sticky top-0 z-10 px-6 py-4 bg-panel border-b border-tint/10 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-fg">Invoice Details</h2>
                            <button
                                onClick={() => setViewInvoiceId(null)}
                                className="p-2 text-fg-3 hover:text-fg hover:bg-tint/10 rounded-lg"
                            >
                                <span className="sr-only">Close</span>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6">
                            <InvoicePreview
                                invoiceId={viewInvoiceId}
                                onPaymentClick={(inv, mode) => {
                                    setPaymentMode(mode);
                                    setPaymentInvoice(inv);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            <PaymentModal
                invoice={paymentInvoice}
                isOpen={!!paymentInvoice}
                onClose={() => setPaymentInvoice(null)}
                onSuccess={() => {
                    setPaymentInvoice(null);
                    fetchInvoices();
                    setViewInvoiceId(null);
                }}
                initialMode={paymentMode}
            />
        </div>
    );
}
